import React from 'react';

import type { EstimateGroup } from '../data/paymentRepository';

import './PaymentSummarySection.css';

interface Props {
  /**
   * 見積もり内訳グループ。
   * - 'plan': そのまま `name` を表示
   * - 'work': `{name}の修繕合計` を表示
   * - 'commission': 表示はしない（下段の「事務手数料」行で `commissionAmount` を使う）
   */
  estimateGroups?: EstimateGroup[];
  /** 小計（事務手数料を含まない税抜合計） */
  subtotalExcludingCommission: number;
  /** 事務手数料額。0 のときは行を表示しない */
  commissionAmount: number;
  /** 消費税額 */
  taxAmount: number;
  /** お支払い合計（税込） */
  totalAmount: number;
}

const formatAmount = (amount: number): string => `${amount.toLocaleString()}円`;

/**
 * お支払い内容の確認画面に表示する内訳セクション。
 *
 * PPTX指示 0506 スライド12:
 * - セクション見出し: 「お支払い内容」
 * - お支払総額（top の total-row）は廃止
 * - 内訳: estimateGroups の plan/work を表示 → 小計 → 事務手数料 → 消費税 → お支払い合計（大）
 * - 「お支払い合計」のフォントサイズを大きくする
 *
 * 全支払方法（現金 / クレジット / 請求書 / 後払い）の確認画面で共通利用する。
 */
const PaymentSummarySection: React.FC<Props> = ({
  estimateGroups,
  subtotalExcludingCommission,
  commissionAmount,
  taxAmount,
  totalAmount,
}) => {
  // estimateGroups から plan / work のみ抽出（commission は別途下段で表示する）。
  const breakdownGroups = estimateGroups?.filter((g) => g.type !== 'commission') ?? [];

  return (
    <section className="confirm-section">
      <h3 className="section-title">お支払い内容</h3>
      <div className="summary-section">
        {breakdownGroups.map((group, index) => {
          const label = group.type === 'work' ? `${group.name}の修繕合計` : group.name;
          return (
            <div className="summary-row" key={`${group.type}-${group.name}-${index}`}>
              <span className="summary-label">{label}</span>
              <span className="summary-value">{formatAmount(group.total)}</span>
            </div>
          );
        })}
        <div className="summary-row">
          <span className="summary-label">小計</span>
          <span className="summary-value">{formatAmount(subtotalExcludingCommission)}</span>
        </div>
        {commissionAmount > 0 && (
          <div className="summary-row">
            <span className="summary-label">事務手数料</span>
            <span className="summary-value">{formatAmount(commissionAmount)}</span>
          </div>
        )}
        <div className="summary-row">
          <span className="summary-label">消費税</span>
          <span className="summary-value">{formatAmount(taxAmount)}</span>
        </div>
        <div className="summary-row payment-total">
          <span className="summary-label">お支払い合計</span>
          <span className="summary-value">{formatAmount(totalAmount)}</span>
        </div>
      </div>
    </section>
  );
};

export default PaymentSummarySection;
